import { useCallback, useEffect, useRef } from 'react'
import type { FormikProps } from 'formik'
import type { CloudMarketplace } from '../../../types'
import { useSelector } from '../../../store/hooks'
import {
  hasAdminRoles,
  Role,
  useTracker,
  getTenantPartnerIdFromState as getTenantPartnerIdFromStateCommon,
} from '@labrav/common'

type PartnerTypeValue = 'user' | 'admin'

const getTenantPartnerIdFromState = (
  state: Record<string, any>,
  userType: PartnerTypeValue = 'user',
  _pathname: string = ''
): string | undefined => {
  return getTenantPartnerIdFromStateCommon(state as any, userType as any)
}

const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000
const ACTIVITY_EVENTS = ['keydown', 'pointerdown', 'input', 'scroll'] as const

const CREATE_TRACKING_BASE = {
  product: 'FLYOUT',
  section: 'private_offers',
  component: 'create_private_offer',
} as const

const PRIVATE_OFFERS_LIST_COMPONENT = 'private_offers_list' as const
const PRIVATE_OFFER_DETAIL_COMPONENT = 'private_offer_detail' as const

/** Scope for list search/filter/row-action events — matches sent-offer events under flyout.offers.* */
export const FLYOUT_PRIVATE_OFFERS_LIST_SCOPE = {
  product: 'flyout',
  section: 'offers',
  route_key: 'private_offers_list',
} as const

/** Scope for private offer detail header actions (clone, offer link, cancel, remind, download PDF). */
export const FLYOUT_PRIVATE_OFFER_DETAIL_SCOPE = {
  product: 'flyout',
  section: 'offers',
  route_key: 'private_offer_detail',
} as const

const SEARCH_FIELD_ACTIONS: Record<string, string> = {
  company_name: 'search_company_name_submitted',
  aws_offer_id: 'search_offer_id_submitted',
}

const ROW_ACTION_KEYS: Record<string, string> = {
  'Copy-private-offer-URL': 'copy_private_offer_url_clicked',
  'View-offer-details': 'view_offer_details_clicked',
  'View-offer-row': 'view_offer_row_clicked',
  'Delete-offer': 'delete_offer_clicked',
}

const HEADER_ACTION_KEYS: Record<string, string> = {
  clone: 'clone_clicked',
  copy_offer_link: 'copy_offer_link_clicked',
  cancel: 'cancel_clicked',
  remind_the_buyer: 'remind_the_buyer_clicked',
  download_pdf: 'download_pdf_clicked',
}

const EXPIRY_DAYS_ACTIONS: Record<string, string> = {
  '5': 'filter_expiring_in_5_days',
  '15': 'filter_expiring_in_15_days',
  '30': 'filter_expiring_in_30_days',
}

const FILTER_LABEL_ACTIONS: Record<string, string> = {
  'Expiring in 5 days': 'filter_expiring_in_5_days',
  'Expiring in 15 days': 'filter_expiring_in_15_days',
  'Expiring in 30 days': 'filter_expiring_in_30_days',
  'Offer sent': 'filter_offer_sent',
  'Offer accepted': 'filter_offer_accepted',
  'Offer viewed': 'filter_offer_viewed',
  Failed: 'filter_failed',
  Cancelled: 'filter_cancelled',
  Deactivated: 'filter_deactivated',
  Expired: 'filter_expired',
}

const STATUS_VALUE_ACTIONS: Record<string, string> = {
  OFFER_EMAIL_SENT: 'filter_offer_sent',
  OFFER_ACCEPTED: 'filter_offer_accepted',
  OFFER_VIEWED: 'filter_offer_viewed',
  OFFER_CREATION_FAILED: 'filter_failed',
  OFFER_RESTRICTED: 'filter_cancelled',
  OFFER_EXPIRED: 'filter_expired',
}

const isExpiryFilterLabel = (label: string) => label.startsWith('Expiring in')

type AbandonmentReason = 'route_change' | 'inactivity'

type UsePrivateOfferCreationTrackingOptions<Values> = {
  cloud?: CloudMarketplace
  productId?: string
  formikProps: FormikProps<Values>
  canSubmit: boolean
}

export type PrivateOfferListTrackingHandlers = {
  trackSearch: (params: { searchQuery: string; searchField: string }) => void
  trackFiltersApplied: (params: {
    expiringInDays: string
    offerStatuses: string[]
    appliedFilterLabels: string[]
  }) => void
  trackFilterRemoved: (params: {
    removedFilter: string
    remainingFilterLabels: string[]
  }) => void
  trackRowAction: (params: {
    actionKey: string
    privateOfferId?: string
    cloudType?: string
    offerStatus?: string
  }) => void
}

export type PrivateOfferViewTrackingHandlers = {
  trackHeaderAction: (params: {
    actionKey: string
    privateOfferId?: string
    cloudType?: string
    offerStatus?: string
  }) => void
}

const getCurrentUrl = () =>
  typeof window === 'undefined'
    ? ''
    : `${window.location.pathname}${window.location.search}`

const firstNonEmpty = (
  ...values: Array<string | null | undefined>
): string | undefined =>
  values.find(
    (value): value is string => typeof value === 'string' && value.trim() !== ''
  )

const createEventId = () => {
  try {
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function'
    ) {
      return crypto.randomUUID()
    }
  } catch {
    // Salesforce Canvas and other non-secure contexts can throw here.
  }
  return `po-list-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const getListTrackingIdentity = (state: Record<string, any>) => {
  const authRoles = (state.authorization?.roles ?? []) as Role[]
  const userType: PartnerTypeValue = hasAdminRoles(authRoles) ? 'admin' : 'user'
  const pathname = typeof window === 'undefined' ? '' : window.location.pathname

  return {
    partnerId: firstNonEmpty(
      getTenantPartnerIdFromState(state, userType, pathname),
      state.AccountData?.[userType]?.partnerData?.partnerId,
      state.authorization?.partnerId
    ),
    partnerUserId: firstNonEmpty(
      state.userProfile?.userProfile?.id,
      state.userProfile?.userProfile?.idpUserId,
      state.authorization?.partnerUserId
    ),
  }
}

const buildPrivateOfferEvent = (
  scope:
    | typeof FLYOUT_PRIVATE_OFFERS_LIST_SCOPE
    | typeof FLYOUT_PRIVATE_OFFER_DETAIL_SCOPE,
  component: string,
  action: string
) => ({
  ...scope,
  component,
  action,
})

const getStatusFilterActions = (
  offerStatuses: string[],
  appliedFilterLabels: string[]
) => {
  const statusLabels = appliedFilterLabels.filter(
    label => !isExpiryFilterLabel(label)
  )
  const fromLabels = statusLabels
    .map(label => FILTER_LABEL_ACTIONS[label])
    .filter((action): action is string => Boolean(action))

  if (fromLabels.length > 0) {
    return fromLabels
  }

  return offerStatuses
    .map(status => STATUS_VALUE_ACTIONS[status])
    .filter((action): action is string => Boolean(action))
}

const usePrivateOfferEventTracker = (
  scope:
    | typeof FLYOUT_PRIVATE_OFFERS_LIST_SCOPE
    | typeof FLYOUT_PRIVATE_OFFER_DETAIL_SCOPE,
  component: string
) => {
  const tracker = useTracker()
  const { partnerId, partnerUserId } = useSelector(getListTrackingIdentity)

  return useCallback(
    (action: string, extra?: Record<string, unknown>) => {
      try {
        tracker?.track({
          ...buildPrivateOfferEvent(scope, component, action),
          ...extra,
          ...(partnerId ? { partner_id: partnerId } : {}),
          ...(partnerUserId ? { partner_user_id: partnerUserId } : {}),
          event_id: createEventId(),
        })
      } catch {
        // Tracking must never block search, filter, row, or header actions.
      }
    },
    [tracker, partnerId, partnerUserId, scope, component]
  )
}

/**
 * Segment tracking for private offer list search/filter/row actions.
 * Each search field, expiry/status filter, row click, and row-menu action
 * emits its own action so dashboards can be built per event (company name vs
 * offer ID, 5/15/30 days, each offer status, row view, and copy/view/delete).
 */
export const usePrivateOfferListTracking =
  (): PrivateOfferListTrackingHandlers => {
    const trackListEvent = usePrivateOfferEventTracker(
      FLYOUT_PRIVATE_OFFERS_LIST_SCOPE,
      PRIVATE_OFFERS_LIST_COMPONENT
    )

    const trackSearch = useCallback(
      ({
        searchQuery,
        searchField,
      }: {
        searchQuery: string
        searchField: string
      }) => {
        const trimmedQuery = searchQuery.trim()
        if (!trimmedQuery) return

        const action = SEARCH_FIELD_ACTIONS[searchField] ?? 'search_submitted'

        trackListEvent(action, {
          searchQuery: trimmedQuery,
          searchField,
        })
      },
      [trackListEvent]
    )

    const trackFiltersApplied = useCallback(
      ({
        expiringInDays,
        offerStatuses,
        appliedFilterLabels,
      }: {
        expiringInDays: string
        offerStatuses: string[]
        appliedFilterLabels: string[]
      }) => {
        if (!expiringInDays && offerStatuses.length === 0) return

        const actions = new Set<string>()
        const expiryAction = EXPIRY_DAYS_ACTIONS[expiringInDays]
        if (expiryAction) {
          actions.add(`${expiryAction}_applied`)
        }

        getStatusFilterActions(offerStatuses, appliedFilterLabels).forEach(
          action => {
            actions.add(`${action}_applied`)
          }
        )

        actions.forEach(action => {
          trackListEvent(action, {
            expiringInDays: expiringInDays || undefined,
            offerStatuses,
            appliedFilterLabels,
          })
        })
      },
      [trackListEvent]
    )

    const trackFilterRemoved = useCallback(
      ({
        removedFilter,
        remainingFilterLabels,
      }: {
        removedFilter: string
        remainingFilterLabels: string[]
      }) => {
        const filterKey = FILTER_LABEL_ACTIONS[removedFilter] ?? 'filter'
        trackListEvent(`${filterKey}_removed`, {
          removedFilter,
          remainingFilterLabels,
        })
      },
      [trackListEvent]
    )

    const trackRowAction = useCallback(
      ({
        actionKey,
        privateOfferId,
        cloudType,
        offerStatus,
      }: {
        actionKey: string
        privateOfferId?: string
        cloudType?: string
        offerStatus?: string
      }) => {
        const trimmedKey = actionKey.trim()
        if (!trimmedKey) return

        const action = ROW_ACTION_KEYS[trimmedKey] ?? 'row_action_clicked'

        trackListEvent(action, {
          actionKey: trimmedKey,
          ...(privateOfferId ? { privateOfferId } : {}),
          ...(cloudType ? { cloudType } : {}),
          ...(offerStatus ? { offerStatus } : {}),
        })
      },
      [trackListEvent]
    )

    return {
      trackSearch,
      trackFiltersApplied,
      trackFilterRemoved,
      trackRowAction,
    }
  }

/**
 * Segment tracking for private offer detail header actions. Each button
 * (clone, copy offer link, cancel, remind the buyer, download PDF) emits
 * its own action so dashboards can be built per click.
 */
export const usePrivateOfferViewTracking =
  (): PrivateOfferViewTrackingHandlers => {
    const trackViewEvent = usePrivateOfferEventTracker(
      FLYOUT_PRIVATE_OFFER_DETAIL_SCOPE,
      PRIVATE_OFFER_DETAIL_COMPONENT
    )

    const trackHeaderAction = useCallback(
      ({
        actionKey,
        privateOfferId,
        cloudType,
        offerStatus,
      }: {
        actionKey: string
        privateOfferId?: string
        cloudType?: string
        offerStatus?: string
      }) => {
        const trimmedKey = actionKey.trim()
        if (!trimmedKey) return

        const action = HEADER_ACTION_KEYS[trimmedKey] ?? 'header_action_clicked'

        trackViewEvent(action, {
          actionKey: trimmedKey,
          ...(privateOfferId ? { privateOfferId } : {}),
          ...(cloudType ? { cloudType } : {}),
          ...(offerStatus ? { offerStatus } : {}),
        })
      },
      [trackViewEvent]
    )

    return { trackHeaderAction }
  }

/** Segment tracking for the V2 private offer create/edit form. */
export const usePrivateOfferCreationTracking = <Values>({
  cloud,
  productId,
  formikProps,
  canSubmit,
}: UsePrivateOfferCreationTrackingOptions<Values>) => {
  const tracker = useTracker()
  const formCompletedRef = useRef(false)
  const abandonmentTrackedRef = useRef(false)
  const isMountedRef = useRef(true)
  const submitInProgressRef = useRef(false)
  const initialUrlRef = useRef(getCurrentUrl())

  const trackAbandonment = useCallback(
    (abandonmentReason: AbandonmentReason) => {
      if (formCompletedRef.current || abandonmentTrackedRef.current) return
      abandonmentTrackedRef.current = true
      tracker?.track({
        ...CREATE_TRACKING_BASE,
        action: 'abandoned',
        cloudProvider: cloud,
        productId,
        abandonmentReason,
      })
    },
    [cloud, productId, tracker]
  )

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>
    isMountedRef.current = true

    const resetInactivityTimer = () => {
      clearTimeout(timeoutId)
      if (abandonmentTrackedRef.current || formCompletedRef.current) return
      timeoutId = setTimeout(() => {
        if (isMountedRef.current) {
          trackAbandonment('inactivity')
        }
      }, INACTIVITY_TIMEOUT_MS)
    }

    ACTIVITY_EVENTS.forEach(eventName =>
      window.addEventListener(eventName, resetInactivityTimer, {
        passive: true,
      })
    )
    resetInactivityTimer()

    return () => {
      isMountedRef.current = false
      clearTimeout(timeoutId)
      ACTIVITY_EVENTS.forEach(eventName =>
        window.removeEventListener(eventName, resetInactivityTimer)
      )
    }
  }, [trackAbandonment])

  useEffect(
    () => () => {
      if (getCurrentUrl() !== initialUrlRef.current) {
        trackAbandonment('route_change')
      }
    },
    [trackAbandonment]
  )

  const handleSubmitPrivateOffer = useCallback(async () => {
    if (!canSubmit || submitInProgressRef.current) return

    submitInProgressRef.current = true
    try {
      const errors = await formikProps.validateForm()
      if (Object.keys(errors).length > 0) {
        await formikProps.submitForm()
        return
      }

      tracker?.track({
        ...CREATE_TRACKING_BASE,
        action: 'send_to_buyer_submitted',
        cloudProvider: cloud,
        productId,
      })
      formCompletedRef.current = true
      await formikProps.submitForm()
    } finally {
      submitInProgressRef.current = false
    }
  }, [canSubmit, cloud, formikProps, productId, tracker])

  return { handleSubmitPrivateOffer }
}
